import { useCallback, useEffect, useRef, useState } from "react";
import type { QueryDocumentSnapshot } from "firebase/firestore";

import {
  fetchAccountConnectionsPage,
  type AccountConnectionDirection,
} from "../lib/follows";
import type { PublicProfileRecord } from "../lib/publicProfiles";

const CONNECTION_PAGE_SIZE = 20;

export function useAccountConnections(
  accountId: string | null | undefined,
  direction: AccountConnectionDirection
) {
  const currentAccountId = String(accountId ?? "").trim();
  const [accounts, setAccounts] = useState<PublicProfileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const cursorRef = useRef<QueryDocumentSnapshot | null>(null);
  const generationRef = useRef(0);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const generation = ++generationRef.current;
    let active = true;
    cursorRef.current = null;
    loadingMoreRef.current = false;
    setAccounts([]);
    setLoading(!!currentAccountId);
    setLoadingMore(false);
    setHasMore(false);
    setError(null);

    if (!currentAccountId) {
      setLoading(false);
      return () => { active = false; };
    }

    void fetchAccountConnectionsPage({
      accountId: currentAccountId,
      direction,
      pageSize: CONNECTION_PAGE_SIZE,
    }).then((page) => {
      if (!active || generation !== generationRef.current) return;
      setAccounts(page.accounts);
      cursorRef.current = page.cursor;
      setHasMore(page.hasMore);
    }).catch((nextError) => {
      if (!active || generation !== generationRef.current) return;
      setError(nextError);
    }).finally(() => {
      if (active && generation === generationRef.current) setLoading(false);
    });

    return () => { active = false; };
  }, [currentAccountId, direction, retryKey]);

  const loadMore = useCallback(async () => {
    if (!currentAccountId || !hasMore || loadingMoreRef.current || !cursorRef.current) return;
    const generation = generationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setError(null);

    try {
      const page = await fetchAccountConnectionsPage({
        accountId: currentAccountId,
        direction,
        cursor: cursorRef.current,
        pageSize: CONNECTION_PAGE_SIZE,
      });
      if (generation !== generationRef.current) return;
      setAccounts((previous) => {
        const byId = new Map(previous.map((account) => [account.identityId || account.id, account]));
        page.accounts.forEach((account) => byId.set(account.identityId || account.id, account));
        return Array.from(byId.values());
      });
      cursorRef.current = page.cursor;
      setHasMore(page.hasMore);
    } catch (nextError) {
      if (generation === generationRef.current) setError(nextError);
    } finally {
      if (generation === generationRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [currentAccountId, direction, hasMore]);

  const retry = useCallback(() => setRetryKey((value) => value + 1), []);

  return { accounts, loading, loadingMore, hasMore, error, loadMore, retry };
}
